import { ComponentProps, ReactNode } from 'react';
import { TypeOptions, ToastContentProps } from 'react-toastify';

import { Button, Icon, IconNames, Text } from '@pukka/ui-components';
import { twMerge } from 'tailwind-merge';

const backgrounds: Record<TypeOptions, string> = {
  success: 'bg-green-100',
  error: 'bg-red-100',
  info: 'bg-light-blue-100',
  warning: 'bg-yellow-100',
  default: 'bg-neutral-600',
};

const ICON_ATTR: Record<TypeOptions, { icon: IconNames; color: ComponentProps<typeof Icon>['color'] }> = {
  success: {
    icon: 'correct',
    color: 'green',
  },
  error: {
    icon: 'wrong',
    color: 'red',
  },
  info: {
    icon: 'help',
    color: 'blue',
  },
  warning: {
    icon: 'error',
    color: 'yellow',
  },
  default: {
    icon: 'help',
    color: 'blue',
  },
};

interface ToastProps extends Partial<ToastContentProps> {
  readonly title?: ReactNode;
  readonly children?: ReactNode;
  readonly type?: TypeOptions;
  readonly className?: string;
}

function MultiLinerToast({ title, children, type = 'default', className, closeToast, toastProps }: ToastProps) {
  return (
    <div
      role="log"
      data-toastid={toastProps?.toastId}
      className={twMerge(backgrounds[type], 'flex flex-row items-center gap-3 rounded-xl px-3 py-2 shadow-lg', className)}
    >
      <Icon size="lg" {...ICON_ATTR[type]} />
      <div className="flex-1">
        <Text weight="medium" variant="caption-2" color="primary">
          {title}
        </Text>
        <Text variant="caption-2" className="text-neutral-700">
          {children}
        </Text>
      </div>
      <Button variant="nude" size="none" className="-mr-0.5 rounded-sm p-0.5" onClick={closeToast}>
        <Icon icon="close" size="sm" color="black" />
      </Button>
    </div>
  );
}

function OneLinerToast({ children, type = 'default', className, closeToast, toastProps }: Omit<ToastProps, 'title'>) {
  return (
    <div
      role="log"
      data-toastid={toastProps?.toastId}
      className={twMerge(backgrounds[type], 'flex flex-row items-center gap-3 rounded-md p-3 shadow-lg', className)}
    >
      <Icon size="lg" {...ICON_ATTR[type]} />
      <Text variant="caption-2" color="primary" className="flex-1">
        {children}
      </Text>
      <Button variant="nude" size="none" className="-mr-0.5 rounded-sm p-0.5" onClick={closeToast}>
        <Icon icon="close" size="sm" color="black" />
      </Button>
    </div>
  );
}

export { MultiLinerToast, OneLinerToast };
